import {
  Actions,
  Breadcrumb,
  Button,
  ErrorAlert,
  getCategories,
  Input,
  Modal,
  modalHandler,
  ModalOpenBtn,
  PageHero,
  success,
  SwitchInput,
  Table,
  TagIcon,
  updateList,
  UploadImageCard,
  useCanShow,
  useCustomForm,
  useDelete,
  useFetchData,
  useState,
  useTableColumns,
} from "@";
import { classificationsFillForm, classificationsFilter } from "../lib/helpers";
import { classificationsColumns } from "../components/table-columns";
import {
  classificationsDefaultValues,
  classificationsSchema,
} from "../lib/validation";
import { saveClassifications } from "../api";

const Classifications = () => {
  // State variables for search input and edit mode
  const [search, setSearch] = useState("");
  const [isEdit, setIsEdit] = useState(false);

  // Custom form hook for handling form state and validation
  const {
    reset,
    watch,
    errors,
    register,
    setValue,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
  } = useCustomForm(classificationsDefaultValues, classificationsSchema);

  // Fetch data hook for retrieving classifications data
  const { data, setData, isLoading, error, fetchData } = useFetchData({
    noPgn: true,
    fun: getCategories,
  });

  // Delete handler for removing classifications
  const { deleteHandler } = useDelete(data, setData, "classifications");

  // Filtered items based on search input
  const filteredItems = classificationsFilter(data, search);

  // Table columns with actions for edit and delete
  const columns = useTableColumns(classificationsColumns, (row) => (
    <>
      {useCanShow("editAssociationData") && (
        <Actions
          show
          notShow
          row={row}
          edit={editHandler}
          delete={deleteHandler}
        />
      )}
    </>
  ));

  // Function to clear the form and reset state
  const clearForm = () => {
    setIsEdit(false);
    reset(classificationsDefaultValues);
  };

  // Submit handler for saving classifications
  const submit = async (form) => {
    try {
      const { data: res } = await saveClassifications(form);
      setData(updateList(data, res));
      success("تم الحفظ بنجاح");
      modalHandler();
      clearForm();
    } catch (error) {}
  };

  // Edit handler for populating the form with existing data
  const editHandler = (item) => {
    reset(classificationsFillForm(item));
    setIsEdit(true);
    modalHandler();
  };

  return (
    <>
      {/* Error alert component to show errors and reload data */}
      <ErrorAlert reload={fetchData} show={error} />
      <Breadcrumb links={[{ name: "تصنيفات المشاريع", link: "" }]} />

      {/* Modal for adding or editing classification */}
      <Modal close={clearForm} title={isEdit ? "تعديل" : "اضافة"}>
        <form onSubmit={handleSubmit(submit)} className="relative p-2 md:p-5">
          {/* Input for classification name */}
          <Input
            style="mb-5"
            label="الاسم"
            error={errors.Name}
            input={register("Name")}
          />

          {/* Input for classification order */}
          <Input
            main={0}
            style="mb-5"
            type="number"
            label="الترتيب"
            error={errors.Order}
            input={register("Order")}
          />


          {/* Upload component for classification image */}
          <UploadImageCard
            show
            title="صورة التصنيف"
            src={watch("imageFullUrl")}
            success={isSubmitSuccessful}
            error={errors.Image?.message}
            upload={(file) => setValue("Image", file)}
          />

          {/* Upload component for Banner */}
          <UploadImageCard
            show
            title="صورة البانر"
            src={watch("bannerFullUrl")}
            success={isSubmitSuccessful}
            error={errors.Banner?.message}
            upload={(file) => setValue("Banner", file)}
          />


          {/* Checkbox group for classification options */}
          <div className="mb-5 grid grid-cols-1 gap-5">
            <SwitchInput
              label="تفعيل التصنيف"
              checked={watch("IsActive")}
              change={(value) => setValue("IsActive", value)}
              isBordered={true}
            />
            <SwitchInput
              label="عرض في الصفحة الرئيسية"
              checked={watch("ShowOnHomePage")}
              change={(value) => setValue("ShowOnHomePage", value)}
              isBordered={true}
            />
            <SwitchInput
              label="عرض في التبرع السريع"
              checked={watch("ShowOnFastDonation")}
              change={(value) => setValue("ShowOnFastDonation", value)}
              isBordered={true}
            />
          </div>

          {/* Submit button for the form */}
          <Button isLoading={isSubmitting} type="submit" title="حفظ البيانات" />
        </form>
      </Modal>

      {/* Main content area displaying the table of classifications */}
      <main className="table-card relative">
        <PageHero
          eyebrow="الإعدادات / التصنيفات"
          title="تصنيفات المشاريع"
          count={filteredItems?.length}
          icon={TagIcon}
          hideCount={isLoading}
        >
          {/* Search input for filtering classifications */}
          <input
            className="single-search-input"
            type="search"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث عن قسم"
            value={search}
          />

          {/* Button to open modal for adding a new classification */}
          {useCanShow("editAssociationData") && (
            <ModalOpenBtn title="اضافة قسم" />
          )}
        </PageHero>

        {/* Table component to display classification data */}
        <Table
          localPg
          columns={columns}
          data={filteredItems}
          isLoading={isLoading}
        />
      </main>
    </>
  );
};

export default Classifications;
